import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise16Regular,
  CalendarClock20Regular,
  Checkmark16Regular,
  ClipboardTask20Regular,
  Document20Regular,
} from '@fluentui/react-icons';
import { getActiveRoleFromToken } from '@moko/utils/src/authHelper';
import { useTenant } from '../TenantProvider';
import TitledCard from '../Layout/TitledCard';
import useFetch from '../hooks/useFetch';
import {
  deadlineErrorMessage,
  deadlineRequestError,
  defaultTenantZone,
  findSla,
  formatDeadlineInstant,
  outcomeText,
  parseWorkSlas,
  showOriginalDue,
  showOriginalOutcome,
  sourceText,
  type WorkSlaRow,
} from './deadlineModel';

export type GetWorkSlas = (
  abortController: AbortController,
  workId: number
) => Promise<unknown>;

type DeadlinePayload = {
  rows: WorkSlaRow[];
  fetchedAt: string;
};

const useStyles = makeStyles({
  card: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'start',
    columnGap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} 0`,
    borderBottom: `1px dashed ${tokens.colorNeutralStroke2}`,
    ':first-child': { paddingTop: 0 },
    ':last-child': { borderBottom: 'none', paddingBottom: 0 },
  },
  tile: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    borderRadius: '9px',
    flexShrink: 0,
  },
  tileSuccess: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
  },
  tileDanger: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
  },
  tileWarning: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    color: tokens.colorStatusWarningForeground1,
  },
  tileInformative: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  tileSubtle: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
    minWidth: 0,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
    minWidth: 0,
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'currentColor',
  },
  details: {
    marginTop: tokens.spacingVerticalXXS,
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalMNudge})`,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    columnGap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
});

type OutcomeTone = 'success' | 'danger' | 'warning' | 'informative' | 'subtle';

const outcomeTone = (outcome: string | null): OutcomeTone => {
  if (outcome === 'Met') return 'success';
  if (outcome === 'Overdue') return 'danger';
  if (outcome === 'Missed') return 'warning';
  if (outcome === 'Pending') return 'informative';
  return 'subtle';
};

const canSeeExplanation = () => {
  const role = getActiveRoleFromToken();
  return role === 'FMSManager.Access' || role === 'FMSPortal.AdminAccess';
};

const DeadlineEntry = ({
  title,
  icon,
  missing,
  row,
  zone,
  showExplanation,
}: {
  title: string;
  icon: ReactNode;
  missing: string;
  row: WorkSlaRow | null;
  zone: ReturnType<typeof defaultTenantZone>;
  showExplanation: boolean;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const due = row ? formatDeadlineInstant(row.dueAt, zone) : '';
  const achieved = row ? formatDeadlineInstant(row.achievedAt, zone) : '';
  const originalDue = row ? formatDeadlineInstant(row.originalDueAt, zone) : '';
  const explanation =
    showExplanation && row?.explanation ? row.explanation : null;
  const source = sourceText(row?.dueDateSource ?? null);
  const meaning = outcomeText(row?.outcome ?? null);
  const originalMeaning = outcomeText(row?.originalOutcome ?? null);
  const tone = outcomeTone(row?.outcome ?? null);
  const tileByTone: Record<OutcomeTone, string> = {
    success: styles.tileSuccess,
    danger: styles.tileDanger,
    warning: styles.tileWarning,
    informative: styles.tileInformative,
    subtle: styles.tileSubtle,
  };

  return (
    <div className={styles.row}>
      <span className={mergeClasses(styles.tile, tileByTone[tone])}>
        {icon}
      </span>
      <div className={styles.text}>
        <Text weight="semibold">{title}</Text>
        {!row && (
          <Text size={200} className={styles.quiet}>
            {missing}
          </Text>
        )}
        {row && (
          <>
            <Text size={200}>
              {`${due || t('deadlineTimeMissing', 'Due time not recorded')} · ${
                zone.label
              }`}
            </Text>
            {meaning && meaning !== row.outcome && (
              <Text size={200} className={styles.quiet}>
                {meaning}
              </Text>
            )}
            {achieved && (
              <div className={styles.metaRow}>
                <Checkmark16Regular />
                <Text size={200}>
                  {t('achievedAt', 'Achieved')} {achieved}
                </Text>
              </div>
            )}
            {showOriginalDue(row) && (
              <Text size={200} className={styles.quiet}>
                {t('originalDue', 'Original due')} {originalDue}
              </Text>
            )}
            {showOriginalOutcome(row) && (
              <Text size={200} className={styles.quiet}>
                {t('originalOutcome', 'Original outcome')} {row.originalOutcome}
                {originalMeaning && originalMeaning !== row.originalOutcome
                  ? `. ${originalMeaning}`
                  : ''}
              </Text>
            )}
            {(source || explanation) && (
              <Accordion collapsible className={styles.details}>
                <AccordionItem value="details">
                  <AccordionHeader size="small">
                    {t('details', 'Details')}
                  </AccordionHeader>
                  <AccordionPanel>
                    {source && (
                      <div className={styles.detailRow}>
                        <Text size={200} weight="semibold">
                          {t('deadlineSource', 'Source')}:
                        </Text>
                        <Text size={200}>{source}</Text>
                      </div>
                    )}
                    {explanation && (
                      <div className={styles.detailRow}>
                        <Text size={200} weight="semibold">
                          {t('explanation', 'Explanation')}:
                        </Text>
                        <Text size={200}>{explanation}</Text>
                      </div>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}
          </>
        )}
      </div>
      {row?.outcome && (
        <Badge
          appearance="tint"
          color={tone}
          icon={<span className={styles.dot} />}
        >
          {row.outcome}
        </Badge>
      )}
    </div>
  );
};

const DeadlineSkeleton = () => {
  const styles = useStyles();
  return (
    <div className={styles.row}>
      <SkeletonItem
        shape="square"
        size={36}
        style={{ width: '34px', height: '34px' }}
      />
      <div className={styles.text}>
        <SkeletonItem size={16} style={{ width: '120px' }} />
        <SkeletonItem size={12} style={{ width: '180px' }} />
      </div>
    </div>
  );
};

const WorkDeadlines = ({
  workId,
  getWorkSlas,
  refreshKey = 0,
}: {
  workId: number;
  getWorkSlas: GetWorkSlas;
  refreshKey?: number;
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { tenant } = useTenant();
  const zone = useMemo(() => defaultTenantZone(tenant), [tenant]);
  const showExplanation = canSeeExplanation();
  const deadlines = useFetch<DeadlinePayload>({
    queryKey: [workId, refreshKey, zone.timeZone],
    queryFn: async (abortController) => {
      try {
        const payload = await getWorkSlas(abortController, workId);
        const rows = parseWorkSlas(payload);
        if (!rows) {
          throw new Error('Deadlines could not be read.');
        }
        return { rows, fetchedAt: new Date().toISOString() };
      } catch (error) {
        if (abortController.signal.aborted) throw error;
        throw deadlineRequestError(error);
      }
    },
  });

  const refreshed = deadlines.data
    ? formatDeadlineInstant(deadlines.data.fetchedAt, zone)
    : '';
  const site = deadlines.data ? findSla(deadlines.data.rows, 'Site') : null;
  const results = deadlines.data
    ? findSla(deadlines.data.rows, 'Results')
    : null;

  const refresh = (
    <Button
      appearance="subtle"
      size="small"
      icon={<ArrowClockwise16Regular />}
      disabled={deadlines.loading}
      onClick={() => deadlines.refetch()}
    >
      {t('refresh', 'Refresh')}
    </Button>
  );

  return (
    <TitledCard
      title={t('deadlines', 'Deadlines')}
      icon={<CalendarClock20Regular />}
      actions={refresh}
    >
      <div className={styles.card} aria-busy={deadlines.loading}>
        {deadlines.loading && (
          <Skeleton aria-label={t('loadingDeadlines', 'Loading deadlines')}>
            <div className={styles.list}>
              <DeadlineSkeleton />
              <DeadlineSkeleton />
            </div>
            <SkeletonItem
              size={12}
              style={{ width: '180px', marginTop: tokens.spacingVerticalM }}
            />
          </Skeleton>
        )}
        {!deadlines.loading && deadlines.error && !deadlines.data && (
          <>
            <Text role="alert">{deadlineErrorMessage(deadlines.error)}</Text>
            <Button
              appearance="subtle"
              size="small"
              disabled={deadlines.loading}
              onClick={() => deadlines.refetch()}
            >
              {t('retry', 'Retry')}
            </Button>
          </>
        )}
        {!deadlines.loading && deadlines.data && (
          <>
            {deadlines.error && (
              <Text role="alert">{deadlines.error.message}</Text>
            )}
            <div className={styles.list}>
              <DeadlineEntry
                title={t('inspectionDue', 'Inspection due')}
                icon={<ClipboardTask20Regular />}
                missing={t(
                  'inspectionDeadlineMissing',
                  'Inspection deadline not recorded'
                )}
                row={site}
                zone={zone}
                showExplanation={showExplanation}
              />
              <DeadlineEntry
                title={t('resultsDue', 'Results due')}
                icon={<Document20Regular />}
                missing={t(
                  'resultsDeadlineMissing',
                  'Results deadline not recorded'
                )}
                row={results}
                zone={zone}
                showExplanation={showExplanation}
              />
            </div>
            <Text className={styles.quiet} size={200}>
              {t('deadlinesUpdated', 'Updated {{time}} {{zone}}', {
                time: refreshed,
                zone: zone.label,
              })}
            </Text>
          </>
        )}
      </div>
    </TitledCard>
  );
};

export default WorkDeadlines;
